import merge from "lodash/merge";
import buildRaGraphqlDataProvider from "ra-data-graphql";
import {
  DELETE,
  DELETE_MANY,
  LegacyDataProvider,
  UPDATE,
  UPDATE_MANY
} from "react-admin";
import { buildQueryFactory } from "./buildQuery";
import { Options, OurOptions } from "./types";
import qs from "qs";

import { makeIntrospectionOptions } from "./utils/makeIntrospectionOptions";

export const defaultOurOptions: OurOptions = {
  queryDialect: "typegraphql"
};

export const defaultOptions: Options = {
  clientOptions: { uri: "/graphql" },
  ...defaultOurOptions
};

const fetchCustomUrl = async (
  fullUrl: string,
  accessToken: string,
  dataName: string,
  totalName: string
) => {
  return fetch(fullUrl, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      accepts: "application/json",
      "content-type": "application/json"
    }
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then((data) => {
      return {
        data: data[dataName],
        total: data[totalName]
      };
    })
    .catch((error) => {
      console.error("There was a problem with the fetch operation:", error);
      // @ts-ignore
      if (window?.ReactNativeWebView) {
        const action = {
          type: "error.dataProvider",
          payload: error
        };
        // @ts-ignore
        window?.ReactNativeWebView?.postMessage(JSON.stringify(action));
      }

      return Promise.reject(
        "The dataProvider threw an error. It should return a rejected Promise instead."
      );
    });
};

const buildDataProvider = async (
  options: Options,
  url: string,
  accessToken: string
): Promise<LegacyDataProvider> => {
  const fullOptions = merge({}, defaultOptions, options);
  return buildRaGraphqlDataProvider(
    merge(
      {},
      {
        buildQuery: buildQueryFactory,
        introspection: makeIntrospectionOptions(fullOptions)
      },
      fullOptions
    )
  )
    .then((graphQLDataProvider) => {
      return (
        fetchType: string,
        resource: string,
        params: { [key: string]: any }
      ): Promise<any> => {
        if (
          (resource === "CustomUser" ||
            resource === "CustomCustomer" ||
            resource === "CustomArtist") &&
          fetchType === "GET_LIST"
        ) {
          const fullUrl = `${url}/v1/full-text-search/${
            params.filter["q"] || ""
          }?${qs.stringify({
            resource: resource.substring(6),
            pagination: JSON.stringify(params.pagination) || "",
            sort: params.sort ? JSON.stringify(params.sort) : ""
          })}`;
          return fetchCustomUrl(fullUrl, accessToken, "users", "total");
        }

        if (resource === "Appointment-service-reporting") {
          const fullUrl = `${url}/v1/reporting/appointment-services?${qs.stringify(
            {
              locationIds: params.meta.locationIdsString,
              startTime: params.meta.startTimeString,
              pagination: JSON.stringify(params.pagination) || ""
            }
          )}`;

          return fetchCustomUrl(fullUrl, accessToken, "data", "total");
        }

        if (resource === "Tipping-reporting") {
          const fullUrl = `${url}/v1/reporting/tipping?${qs.stringify({
            locationIds: params.meta.locationIdsString,
            startTime: params.meta.startTimeString,
            pagination: JSON.stringify(params.pagination) || ""
          })}`;

          return fetchCustomUrl(fullUrl, accessToken, "data", "total");
        }

        if (resource === "Transactions-reporting") {
          const fullUrl = `${url}/v1/reporting/transactions?${qs.stringify({
            locationIds: params.meta.locationIdsString,
            startTime: params.meta.startTimeString,
            paymentMethod: params.meta.paymentMethod,
            pagination: JSON.stringify(params.pagination) || ""
          })}`;

          return fetchCustomUrl(fullUrl, accessToken, "data", "total");
        }

        if (resource === "Cancelled-appointment-reporting") {
          const fullUrl = `${url}/v1/reporting/cancelled-appointments?${qs.stringify(
            {
              locationIds: params.meta.locationIdsString,
              startTime: params.meta.startTimeString,
              pagination: JSON.stringify(params.pagination) || ""
            }
          )}`;

          return fetchCustomUrl(fullUrl, accessToken, "data", "total");
        }

        if (resource === "CustomUser" || resource === "CustomCustomer") {
          resource = "User";
        } else if (resource === "AddOns") {
          resource = "Service";
        } else if (
          resource === "Appointment-order" ||
          resource === "Walk-in-order"
        ) {
          resource = "Order";
        }
        // Temporary work-around until we make use of updateMany and deleteMany mutations
        if (fetchType === DELETE_MANY) {
          const { ids, ...otherParams } = params;
          return Promise.all(
            params.ids.map((id: string) =>
              graphQLDataProvider(DELETE, resource, {
                id,
                ...otherParams
              })
            )
          ).then((results) => {
            return { data: results.map(({ data }: any) => data.id) };
          });
        }

        if (fetchType === UPDATE_MANY) {
          const { ids, ...otherParams } = params;
          return Promise.all(
            params.ids.map((id: string) =>
              graphQLDataProvider(UPDATE, resource, {
                id,
                ...otherParams
              })
            )
          ).then((results) => {
            return { data: results.map(({ data }: any) => data.id) };
          });
        }
        return graphQLDataProvider(fetchType, resource, params);
      };
    })
    .catch((error) => {
      // @ts-ignore
      if (window?.ReactNativeWebView) {
        const action = {
          type: "error.dataProvider",
          payload: error
        };
        // @ts-ignore
        window?.ReactNativeWebView?.postMessage(JSON.stringify(action));
      }
    });
};

export default buildDataProvider;
